import { useState } from 'react';
import { fetchDownloadFiltersFile } from '../../../apiCalls';
import { buildFiltersObject } from '../../../helpers/buildFiltersObject';
import { ColumnVisibility, ExtensionFile, Items } from '../../../../../../types';
import { DownloadOperationsProps, DownloadOperationsReturn } from '../types';

export function useDownloadOperations({
  view,
  source,
  tab,
  searchItems,
  isAuthenticated,
  authorization,
  isEvidence,
  isMapping,
  mappingContent,
  totalResults,
  columnVisibilityModel,
  getFilterValueRef,
  dispatch,
  onWarning,
  onSuccess,
  onError,
}: DownloadOperationsProps): DownloadOperationsReturn {
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pendingDownloadExt, setPendingDownloadExt] = useState<ExtensionFile | null>(null);

  const titleGrid = isEvidence ? 'Evidence' : 'Summary';

  const performDownloadWithFilters = async (extension: ExtensionFile) => {
    try {
      onWarning('Download is being processed.');
      dispatch({ type: 'SET_LOADING', payload: true });

      const filterValue = getFilterValueRef.current ? getFilterValueRef.current() : [];

      const apiSearchItems: Items[] = searchItems.map(item => ({
        id: item.id,
        name: item.name,
        [item.id]: item.name, 
      }));

      const apiMappingContent = mappingContent
        ? {
            isActive: mappingContent.isActive,
            columVisibilityModelKey: mappingContent.columVisibilityModelKey,
            mappingItems: mappingContent.mappingItems?.map(item => ({
              filterParam: null,
              filterValue: item.id,
            })),
          }
        : undefined;

      await fetchDownloadFiltersFile({
        extension: extension.value,
        columnVisibilityModel: columnVisibilityModel as unknown as ColumnVisibility,
        view,
        source,
        tab,
        searchItems: apiSearchItems,
        titleGrid,
        isAuthenticated,
        authorization,
        isEvidence,
        isMapping,
        mappingContent: apiMappingContent,
        filters: buildFiltersObject(filterValue),
        filterValue,
        totalResults,
      });

      onSuccess('Successfully downloaded!');
    } catch (error) {
      onError('Error downloading file.');
      console.error('Error downloading file:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleDownloadFilesWithFilters = async (extension: ExtensionFile) => {
    setPendingDownloadExt(extension);

    if (searchItems && searchItems.length > 50) {
      onError('Cannot download more than 50 search items. Please reduce your selection.');
      return;
    }

    if (totalResults > 55000) {
      setShowDownloadConfirm(true);
      setPendingDownloadExt(extension);
      return;
    }

    await performDownloadWithFilters(extension);
  };

  const confirmLargeDownload = async () => {
    if (pendingDownloadExt) {
      await performDownloadWithFilters(pendingDownloadExt);
      setPendingDownloadExt(null);
    }
    setShowDownloadConfirm(false);
  };

  return {
    showDownloadConfirm,
    setShowDownloadConfirm,
    handleDownloadFilesWithFilters,
    confirmLargeDownload,
  };
}
